import { ref, computed, ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useJourneyStore } from '@/stores/journeyStore'
import { useRoute } from 'vue-router'
import { LocationQueryValue } from 'vue-router'
import { isValid } from 'date-fns'
import { useMyTravelStore } from '@/stores/myTravelStore'

export function useSearchResultDisplay(isRebooking: ComputedRef<boolean>) {
  const { t } = useI18n()
  const route = useRoute()
  const journeyStore = useJourneyStore()
  const myTravelStore = useMyTravelStore()
  const isDrawerOpen = ref(false)
  const isMobileView = ref(false)

  const heroLabel = computed(() => {
    if (isRebooking.value) {
      if (showOutbound.value) {
        return t('travelPlanner.hero.changeOutbound')
      } else {
        return t('travelPlanner.hero.changeReturn')
      }
    } else {
      if (showOutbound.value) {
        return t('travelPlanner.hero.searchOutbound')
      } else {
        return t('travelPlanner.hero.searchReturn')
      }
    }
  })

  const showOutbound = ref(true)

  const origin = computed(() => journeyStore.OriginStation?.name || '')
  const destination = computed(() => journeyStore.destinationStation?.name || '')
  const hasReturnTravel = computed(() => !!journeyStore.returnDate)
  const passengers = computed(() => journeyStore.passengers || [])

  const outboundTime = computed(() => {
    const selectedTravel = journeyStore.selectedOutboundTravel
    if (!selectedTravel || typeof selectedTravel.routeIndex !== 'number') {
      return ''
    }
    const outboundTravels =
      journeyStore.journeySearchResults?.data?.offer?.travels.find(
        (travel) => travel.direction === 'outbound'
      )
    const route = outboundTravels?.routes?.[selectedTravel.routeIndex]
    return route?.legs?.[0]?.departure_timestamp || ''
  })

  const returnTime = computed(() => {
    const selectedReturnTravel = journeyStore.selectedReturnTravel
    if (!selectedReturnTravel || typeof selectedReturnTravel.routeIndex !== 'number') {
      return ''
    }
    const returnTravels =
      journeyStore.journeySearchResults?.data?.offer?.travels.find(
        (travel) => travel.direction === 'inbound'
      )
    const route = returnTravels?.routes?.[selectedReturnTravel.routeIndex]
    return route?.legs?.[0]?.departure_timestamp || ''
  })

  const outboundDate = computed(() => journeyStore.departureDate || '')
  const returnDate = computed(() => journeyStore.returnDate || '')

  const toggleDrawer = () => {
    isDrawerOpen.value = !isDrawerOpen.value
  }

  const checkMobileView = () => {
    isMobileView.value = window.innerWidth < 674
  }

  const getQueryString = (
    value: LocationQueryValue | LocationQueryValue[]
  ): string | undefined => {
    if (Array.isArray(value)) {
      console.warn('Unexpected array in query parameter:', value)
      return (
        value.find((v): v is string => typeof v === 'string') || undefined
      )
    }
    return typeof value === 'string' ? value : undefined
  }

  const backRoute = computed(() => {
    const query: Record<string, string | undefined> = {
      origin:
        getQueryString(route.query.origin) ||
        journeyStore.OriginStation?._u_i_c_station_code,
      destination:
        getQueryString(route.query.destination) ||
        journeyStore.destinationStation?._u_i_c_station_code,
      outboundDate:
        getQueryString(route.query.outboundDate) ||
        journeyStore.departureDate,
      returnDate:
        getQueryString(route.query.returnDate) ||
        journeyStore.returnDate ||
        undefined,
      lang: getQueryString(route.query.lang) || 'no',
      passengers:
        getQueryString(route.query.passengers) ||
        (journeyStore.passengers.length
          ? JSON.stringify(
              journeyStore.passengers.map((p) => ({ type: p.type }))
            )
          : undefined),
    }
    Object.keys(query).forEach(
      (key) => query[key] === undefined && delete query[key]
    )
    return {
      path: '/reiseplanlegger/reisende',
      query,
    }
  })

  const setupReturnDate = () => {
    if (!journeyStore.returnDate || journeyStore.returnDate === '') {
      const savedReturnDate = sessionStorage.getItem('rebooking_return_date')
      if (savedReturnDate) {
        journeyStore.setReturnDate(savedReturnDate)
      } else {
        const returnDateFromCalendar = route.query.returnDate as string
        if (returnDateFromCalendar) {
          journeyStore.setReturnDate(returnDateFromCalendar)
        }
      }
    }
    
    if (journeyStore.returnDate && myTravelStore.returnDate === '') {
      try {
        myTravelStore.setDates(myTravelStore.outboundDate, journeyStore.returnDate)
      } catch (error) {
        console.error('[DEBUG] SearchResultPage - Error formatting return date:', error)
        
        const date = new Date(journeyStore.returnDate)
        if (isValid(date)) {
          const day = date.getDate().toString().padStart(2, '0')
          const monthNames = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des']
          const month = monthNames[date.getMonth()]
          const formattedDate = `${day}. ${month}`
          
          myTravelStore.$patch({
            returnDate: formattedDate
          })
        } else {
          myTravelStore.$patch({
            returnDate: journeyStore.returnDate
          })
        }
      }
    }
  }

  const showInboundTravels = () => {
    setupReturnDate()
    showOutbound.value = false
  }

  return {
    heroLabel,
    showOutbound,
    origin,
    destination,
    hasReturnTravel,
    passengers,
    outboundTime,
    returnTime,
    outboundDate,
    returnDate,
    isDrawerOpen,
    isMobileView,
    toggleDrawer,
    checkMobileView,
    backRoute,
    showInboundTravels,
    getQueryString
  }
}
